/** @jsx jsx */
import { css, jsx } from '@emotion/react';

import * as React from 'react';

import { Button } from '@coursera/cds-core';
import type { ButtonProps } from '@coursera/cds-core';
import { SpinnerIcon } from '@coursera/cds-icons';
import type { ApiButtonProps } from '@coursera/coursera-ui';
import { API_BEFORE_SEND, API_IN_PROGRESS } from '@coursera/coursera-ui/lib/constants/sharedConstants';

import CdsAuthoringTrackedButton from 'bundles/authoring/common/components/cds/AuthoringTrackedButton';
import { TooltipWrapper } from 'bundles/common/components/Tooltip';
import type { TrackingData } from 'bundles/common/components/withSingleTracked';

import _t from 'i18n!nls/authoring';

export type Props = {
  buttonSize?: 'small' | 'medium';
  onCancelButtonClick?: (event: React.MouseEvent<HTMLButtonElement, MouseEvent>) => void;
  onPrimaryButtonClick?: (event: React.MouseEvent<HTMLButtonElement, MouseEvent>) => void;
  onSecondaryButtonClick?: (event: React.MouseEvent<HTMLButtonElement, MouseEvent>) => void;
  isCancelButtonDisabled?: boolean;
  isPrimaryButtonDisabled?: boolean;
  isSecondaryButtonDisabled?: boolean;
  cancelButtonContents?: string;
  primaryButtonContents?: React.ReactNode;
  secondaryButtonContents?: string;
  secondaryButtonVariant?: ButtonProps['variant'];
  disabledPrimaryExplanation?: string;
  explanationClassName?: string;
  hasStickyFooter?: boolean;
  /**
   * This prop allows you to pass additional props required by ApiButton documented in
   * https://building.coursera.org/coursera-ui/?selectedKind=basic.Button&selectedStory=ApiButton&full=0&addons=0&stories=1&panelRight=0&addonPanel=storybook%2Factions%2Factions-panel
   */
  primaryApiButtonProps?: ApiButtonProps;
  /** This prop allows us to provide a custom primary button to the SlideOut */
  customPrimaryButton?: React.ReactNode;
  alignButtonsRight?: boolean;
  trackingNameForPrimaryButton?: string;
  trackingDataForPrimaryButton?: TrackingData;
};

const sharedStyle = {
  stickyFooter: css`
    position: fixed;
    z-index: 4;
    padding: var(--cds-spacing-300);
    width: 100%;
    background-color: var(--cds-color-neutral-background-primary);
    border-top: 1px solid var(--cds-color-neutral-disabled);
    bottom: 0;
  `,
};

const styles = {
  root: (hasStickyFooter?: boolean) =>
    css({
      display: 'flex',
      justifyContent: 'flex-start',
      marginTop: 'var(--cds-spacing-600)',
      ...(hasStickyFooter && { left: 0 }),
    }),
  footerButton: css`
    margin-right: var(--cds-spacing-300);
  `,
};

const stylesInverted = {
  root: (hasStickyFooter?: boolean) =>
    css({
      display: 'flex',
      justifyContent: 'flex-end',
      marginTop: 'var(--cds-spacing-600)',
      ...(hasStickyFooter && { right: 0 }),
    }),
  footerButton: css`
    margin-left: var(--cds-spacing-300);
  `,
};

const ModalButtonFooter = ({
  buttonSize,
  onCancelButtonClick,
  onPrimaryButtonClick,
  onSecondaryButtonClick,
  isCancelButtonDisabled = false,
  isPrimaryButtonDisabled = false,
  isSecondaryButtonDisabled = false,
  cancelButtonContents = _t('Cancel'),
  primaryButtonContents,
  secondaryButtonContents,
  disabledPrimaryExplanation,
  explanationClassName,
  primaryApiButtonProps,
  secondaryButtonVariant,
  alignButtonsRight = false,
  trackingNameForPrimaryButton,
  trackingDataForPrimaryButton,
  hasStickyFooter,
  customPrimaryButton,
}: Props) => {
  const style = alignButtonsRight ? stylesInverted : styles;
  const primaryBtnToRender =
    customPrimaryButton ||
    (trackingNameForPrimaryButton ? (
      <CdsAuthoringTrackedButton
        variant="primary"
        size={buttonSize}
        onClick={onPrimaryButtonClick}
        disabled={isPrimaryButtonDisabled}
        data-testid="primary-button"
        css={style.footerButton}
        trackingName={trackingNameForPrimaryButton}
        trackingData={trackingDataForPrimaryButton}
      >
        {primaryButtonContents}
      </CdsAuthoringTrackedButton>
    ) : (
      <Button
        variant="primary"
        size={buttonSize}
        onClick={onPrimaryButtonClick}
        disabled={isPrimaryButtonDisabled}
        data-testid="primary-button"
        css={style.footerButton}
      >
        {primaryButtonContents}
      </Button>
    ));

  const primary = (
    <div>
      {!!primaryButtonContents && !primaryApiButtonProps && (
        <TooltipWrapper
          show={isPrimaryButtonDisabled && disabledPrimaryExplanation}
          message={disabledPrimaryExplanation}
          tooltipClassName={explanationClassName}
        >
          {primaryBtnToRender}
        </TooltipWrapper>
      )}
      {!!primaryApiButtonProps && (
        <TooltipWrapper
          show={isPrimaryButtonDisabled && disabledPrimaryExplanation}
          message={disabledPrimaryExplanation}
          tooltipClassName={explanationClassName}
        >
          <Button
            iconPosition="before"
            icon={primaryApiButtonProps?.apiStatus === API_IN_PROGRESS ? <SpinnerIcon /> : undefined}
            onClick={onPrimaryButtonClick}
            disabled={isPrimaryButtonDisabled}
            data-test="primary-api-button"
            size={buttonSize}
            variant="primary"
            css={styles.footerButton}
          >
            {
              (primaryApiButtonProps?.apiStatus === API_IN_PROGRESS
                ? primaryApiButtonProps?.apiStatusAttributesConfig?.label?.[API_IN_PROGRESS]
                : primaryApiButtonProps?.apiStatusAttributesConfig?.label?.[API_BEFORE_SEND]) as string
            }
          </Button>
        </TooltipWrapper>
      )}
    </div>
  );

  const secondary = !!secondaryButtonContents && (
    <Button
      variant={secondaryButtonVariant || 'secondary'}
      size={buttonSize}
      css={style.footerButton}
      onClick={onSecondaryButtonClick}
      data-testid="secondary-button"
      disabled={isSecondaryButtonDisabled}
    >
      {secondaryButtonContents}
    </Button>
  );

  const cancel = !!onCancelButtonClick && (
    <Button
      variant="secondary"
      data-testid="cancel-button"
      css={style.footerButton}
      size={buttonSize}
      onClick={onCancelButtonClick}
      disabled={isCancelButtonDisabled}
    >
      {cancelButtonContents}
    </Button>
  );

  return (
    <div
      data-e2e="modal-button-footer"
      className="slide-out-modal-button-footer"
      css={hasStickyFooter ? [style.root(true), sharedStyle.stickyFooter] : style.root(false)}
    >
      {!alignButtonsRight ? primary : secondary}
      {cancel}
      {!alignButtonsRight ? secondary : primary}
    </div>
  );
};

export default ModalButtonFooter;
